// src/components/PrintableExportSlip.js
import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Box as MuiBox,
} from '@mui/material';

/* ---------- Helpers ---------- */
const toDate = (v) => {
  if (!v) return null;
  try {
    return typeof v?.toDate === 'function' ? v.toDate() : new Date(v);
  } catch {
    return null;
  }
};
const dmy = (v) => {
  const d = toDate(v);
  return d ? d.toLocaleDateString('vi-VN') : 'N/A';
};
const VND = (n) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' })
    .format(Number(n || 0));

/* ---------- Styles ---------- */
const border = '1px solid #d0d0d0';
const head = {
  border,
  fontWeight: 700,
  color: '#000',
  bgcolor: '#ffffff',
  textAlign: 'center',
};
const cell = { border, textAlign: 'center' };

/**
 * Printable Export Slip (chỉ hiển thị; KHÔNG có nút)
 * @param {{ order: any, company?: any }} props
 */
const PrintableExportSlip = React.forwardRef(({ order, company = {} }, ref) => {
  if (!order) return null;

  return (
    <Box
      ref={ref}
      sx={{
        p: 3,
        background: '#fff',
        color: '#000',
        maxWidth: '190mm',
        m: '0 auto',
        fontFamily: 'Arial, sans-serif',
        fontSize: 14,
      }}
    >
      {/* ---------- Header ---------- */}
      <Grid container alignItems="center" sx={{ mb: 3 }}>
        {/* Company info */}
        <Grid item xs={8}>
          <Grid container spacing={1} alignItems="center">
            <Grid item>
              <img
                src={company.logo || '/logo.png'}
                alt="logo"
                style={{ width: 72, height: 72, objectFit: 'contain' }}
              />
            </Grid>
            <Grid item xs>
              <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                {company.name || ''}
              </Typography>
              <Typography variant="body2">Địa chỉ: {company.address || ''}</Typography>
              <Typography variant="body2">
                MST: {company.taxCode || ''} | SĐT: {company.phone || ''}
              </Typography>
             
            </Grid>
          </Grid>
        </Grid>

        {/* Title */}
        <Grid item xs={4} sx={{ textAlign: 'center', mx: 'auto' }}>
          <Typography variant="h4" noWrap sx={{ fontWeight: 700, color: '#ef6c00' }}>
            PHIẾU XUẤT KHO
          </Typography>
          <Typography>
            Số SO: <strong>{order.soNumber || ''}</strong>
          </Typography>
          <Typography>Ngày lập: {dmy(order.soDate)}</Typography>
        </Grid>
      </Grid>

      {/* ---------- Info box ---------- */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f9f9f9' }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Khách hàng:</strong> {order.customerName || ''}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Ngày xuất hàng:</strong> {dmy(order.shippingDate)}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography>
              <strong>Địa chỉ KH:</strong> {order.customerAddress || ''}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Mã KH:</strong> {order.customerCode || ''}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography>
              <strong>Đại diện:</strong> {order.customerRepresentative || ''}{' '}
              {order.customerPosition ? `(Chức vụ: ${order.customerPosition})` : ''}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography>
              <strong>Vận chuyển:</strong>{' '}
              {order.driverName
                ? `Tài xế: ${order.driverName} – Xe: ${order.vehiclePlate || 'N/A'}`
                : 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography>
              <strong>Ghi chú:</strong> {order.note || 'Không có'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* ---------- Table ---------- */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '8%' }} />
            <col style={{ width: '46%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '17.5%' }} />
            <col style={{ width: '17.5%' }} />
          </colgroup>

        <TableHead>
          <TableRow>
            {['STT', 'Tên sản phẩm', 'SL', 'Đơn giá', 'Thành tiền'].map((h) => (
              <TableCell key={h} sx={head}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {(order.items || []).map((it, i) => (
            <TableRow key={i}>
              <TableCell sx={cell}>{i + 1}</TableCell>
              <TableCell sx={{ ...cell, textAlign: 'left' }}>{it.productName}</TableCell>
              <TableCell sx={cell}>{it.quantity}</TableCell>
              <TableCell sx={{ ...cell, textAlign: 'right' }}>{VND(it.exportPrice)}</TableCell>
              <TableCell sx={{ ...cell, textAlign: 'right' }}>
                {VND((it.quantity || 0) * (it.exportPrice || 0))}
              </TableCell>
            </TableRow>
          ))}

          {Array.isArray(order.items) && order.items.length > 0 && (
            <TableRow>
              <TableCell sx={{ ...cell, fontWeight: 700 }} colSpan={2}>Cộng</TableCell>
              <TableCell sx={{ ...cell, fontWeight: 700 }}>
                {(order.items || []).reduce((s, it) => s + Number(it.quantity || 0), 0)}
              </TableCell>
              <TableCell sx={cell}></TableCell>
              <TableCell sx={{ ...cell, textAlign: 'right', fontWeight: 700 }}>
                {VND(
                  (order.items || []).reduce(
                    (s, it) => s + Number(it.quantity || 0) * Number(it.exportPrice || 0),
                    0
                  )
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        </Table>
      </TableContainer>

      {/* ---------- Totals ---------- */}
      <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
        <Grid item xs={12} sm={7} md={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              bgcolor: '#fff3e0',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            {[
              ['Tiền hàng', order.subtotal],
              [`Thuế GTGT (${order.taxRate ?? 0}%)`, order.taxAmount],
              ['Cước vận chuyển', order.shippingFee],
              ['Tổng cộng', order.grandTotal, true],
              ['Đã thanh toán', order.amountPaid, false, 'green'],
              ['Còn lại', order.remainingAmount, true, 'red'],
            ].map(([label, value, bold, color]) => (
              <MuiBox key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant={bold ? 'h6' : 'body2'}>{label}:</Typography>
                <Typography
                  variant={bold ? 'h6' : 'body2'}
                  sx={{ color, fontWeight: bold ? 700 : 400 }}
                >
                  {VND(value)}
                </Typography>
              </MuiBox>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* ---------- Signatures ---------- */}
      <Divider sx={{ my: 4 }} />
      <Grid container justifyContent="space-between" textAlign="center">
        {['Người lập phiếu', 'Người giao hàng', 'Người nhận hàng'].map((name) => (
          <Grid item xs={4} key={name}>
            <Typography sx={{ fontWeight: 700 }}>{name}</Typography>
            <Typography sx={{ fontStyle: 'italic' }}>(Ký, ghi rõ họ tên)</Typography>
            <Box sx={{ height: 96 }} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

export default PrintableExportSlip;
